/* drawingStandards — typed accessor over codegen.json.drawingStandards.
 *
 * Single source of truth for *which* standards / file-formats / public
 * datasets the ingest pipeline + Rule Builder + ontology recognise.
 * No magic strings anywhere else in the TS tree — every consumer
 * imports from this module.
 *
 * Spec 07 §"Ontology surface". Per CLAUDE.md §1 — the enumeration
 * lives in `docs/ontology/codegen.json` and we read it; we never
 * re-declare the list.
 */

import codegen from "../../../docs/ontology/codegen.json";

export type DrawingStandardKind =
  | "file-format"
  | "presentation-standard"
  | "exchange-standard"
  | "dataset";

export interface DrawingStandard {
  id: string;
  label: string;
  kind: DrawingStandardKind;
  citation: string;
  /** Lower-case file extensions, when the standard is a file format. */
  fileExt?: string[];
}

interface RawStandard {
  id: string;
  label: string;
  kind: string;
  citation: string;
  fileExt?: string[];
}

const RAW: ReadonlyArray<RawStandard> = (codegen.drawingStandards ?? []) as ReadonlyArray<RawStandard>;

function isKnownKind(s: string): s is DrawingStandardKind {
  return s === "file-format" || s === "presentation-standard" || s === "exchange-standard" || s === "dataset";
}

export const DRAWING_STANDARDS: ReadonlyArray<DrawingStandard> = RAW.map((r) => ({
  id: r.id,
  label: r.label,
  kind: isKnownKind(r.kind) ? r.kind : "file-format",
  citation: r.citation,
  ...(r.fileExt ? { fileExt: r.fileExt.map((x) => x.toLowerCase()) } : {}),
}));

const BY_ID: ReadonlyMap<string, DrawingStandard> =
  new Map(DRAWING_STANDARDS.map((s) => [s.id, s]));

export function standardById(id: string): DrawingStandard | undefined {
  return BY_ID.get(id);
}

export function standardsByKind(kind: DrawingStandardKind): DrawingStandard[] {
  return DRAWING_STANDARDS.filter((s) => s.kind === kind);
}

/** Identify which standard a file extension belongs to. */
export function standardByFileExt(ext: string): DrawingStandard | undefined {
  const e = ext.toLowerCase().replace(/^\./, "");
  return DRAWING_STANDARDS.find((s) => s.fileExt?.includes(e));
}

/** Pretty citation string for the cockpit / Why-engine — never embed
 *  the dataset name as a literal; route through here. */
export function citationOf(id: string): string {
  const s = BY_ID.get(id);
  return s ? `${s.label} · ${s.citation}` : id;
}

/** All ids, typed as a const tuple for downstream Zod enum derivation. */
export const DRAWING_STANDARD_IDS: ReadonlyArray<string> = DRAWING_STANDARDS.map((s) => s.id);
